import { BaseVisualizer } from './BaseVisualizer';

/**
 * Lightning bolt structure with branching segments
 */
interface LightningBolt {
  segments: { x: number; y: number }[];
  branches: LightningBolt[];
  thickness: number;
  brightness: number;
  color: { h: number; s: number; l: number };
  lifetime: number;
  age: number;
  coilIndex: number;
}

/**
 * Tesla coil structure
 */
interface TeslaCoil {
  x: number;
  y: number;
  energy: number;
  glow: number;
  baseRadius: number;
  targetEnergy: number;
}

/**
 * Tesla Coil Visualizer - Electric lightning bolts reacting to music
 * 
 * Features:
 * - Multiple Tesla coils positioned around the screen
 * - Procedurally generated lightning bolts with branching
 * - Beat-reactive massive lightning bursts
 * - Frequency-based coloring (bass = blue/purple, treble = cyan/white)
 * - Energy-based bolt count and intensity
 * - Dramatic screen flash effects
 * - Intro animation with coils powering up
 * - Idle state with occasional sparks
 */
export class TeslaCoilVisualizer extends BaseVisualizer {
  private coils: TeslaCoil[] = [];
  private bolts: LightningBolt[] = [];
  private maxBolts: number = 80; // Increased for single powerful coil
  private frameCount: number = 0;
  private lastBeatTime: number = 0;
  private flashIntensity: number = 0;
  private visualizerState: 'idle' | 'intro' | 'running' = 'idle';
  private introStartTime: number = 0;
  private introDuration: number = 3.5; // 3.5 seconds intro
  private hasStartedOnce: boolean = false;

  constructor(canvas: HTMLCanvasElement) {
    super(canvas, 180);
    this.initializeCoils();
  }

  /**
   * Initialize Tesla coil positions
   */
  private initializeCoils(): void {
    // Single large Tesla coil in the CENTER of the screen
    const w = this.canvas.width;
    const h = this.canvas.height;
    const baseRadius = 60; // 2x larger than before (was 30)

    this.coils = [
      // Single center orb - perfectly centered
      { x: w / 2, y: h / 2, energy: 0, glow: 0, baseRadius, targetEnergy: 0 },
    ];
  }

  /**
   * Update coil positions on resize
   */
  protected resize(): void {
    super.resize();
    this.initializeCoils();
  }

  /**
   * Main render loop
   */
  protected render(): void {
    this.frameCount++;

    // Handle different states
    if (this.visualizerState === 'idle') {
      this.renderIdleState();
      return;
    }

    // Dark background with slight fade trail
    this.ctx.fillStyle = 'rgba(2, 0, 8, 0.35)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Get intro progress
    const introProgress = this.getIntroProgress();
    const isInIntro = this.isInIntro();

    // Apply intro fade
    if (isInIntro) {
      this.ctx.save();
      this.ctx.globalAlpha = this.easeOutCubic(introProgress);
    }

    // Update coil energy based on audio
    this.updateCoils(introProgress);

    // Spawn lightning bolts based on audio
    this.spawnLightning(introProgress);

    // Update existing bolts
    this.updateBolts();

    // Draw screen flash if needed
    if (this.flashIntensity > 0.01) {
      this.drawScreenFlash();
    }

    // Draw all bolts
    this.drawBolts();

    // Draw Tesla coils
    this.drawCoils(introProgress);

    // Restore context
    if (isInIntro) {
      this.ctx.restore();
    }

    // Check if intro complete
    if (isInIntro && introProgress >= 1) {
      this.visualizerState = 'running';
    }
  }

  /**
   * Update Tesla coil energy levels
   */
  private updateCoils(introProgress: number): void {
    const coil = this.coils[0]; // Single coil

    // During intro, gradually power up coil
    if (this.isInIntro()) {
      const powerProgress = this.easeOutCubic(introProgress);
      coil.targetEnergy = powerProgress * 0.5; // More energy during intro
    } else {
      // Average of all pitches for overall energy
      const avgPitch = this.currentPitches.length > 0 
        ? this.currentPitches.reduce((sum, p) => sum + p, 0) / this.currentPitches.length 
        : 0.4;

      // Base energy from overall energy, average pitch, and loudness
      coil.targetEnergy = this.currentEnergy * 0.5 + avgPitch * 0.3 + this.currentLoudness * 0.2;

      // Beat boost - make it more dramatic
      if (this.currentBeat > 0.7) {
        coil.targetEnergy = Math.min(1, coil.targetEnergy + this.currentBeat * 0.6);
      }
    }

    // Smooth energy transition
    coil.energy += (coil.targetEnergy - coil.energy) * 0.15;
    coil.glow = coil.energy * (0.8 + Math.sin(this.currentTime * 3) * 0.2);
  }

  /**
   * Spawn lightning bolts based on audio
   */
  private spawnLightning(introProgress: number): void {
    // During intro, spawn gradually increasing bolts with ramping intensity
    if (this.isInIntro()) {
      // Stage 1 (25-50%): Small sparks
      if (introProgress > 0.25 && introProgress < 0.5 && this.frameCount % 30 === 0) {
        this.spawnBolt(0, 0.3, 0.3);
      }
      // Stage 2 (50-75%): More sparks, getting bigger
      else if (introProgress >= 0.5 && introProgress < 0.75 && this.frameCount % 15 === 0) {
        this.spawnBolt(0, 0.5, 0.5);
      }
      // Stage 3 (75-90%): Lightning starts branching
      else if (introProgress >= 0.75 && introProgress < 0.9 && this.frameCount % 8 === 0) {
        const boltCount = Math.floor(3 + Math.random() * 2);
        for (let i = 0; i < boltCount; i++) {
          this.spawnBolt(0, 0.7, 0.7);
        }
      }
      // Stage 4 (90-100%): Full power unleashed
      else if (introProgress >= 0.9 && this.frameCount % 4 === 0) {
        const boltCount = Math.floor(5 + Math.random() * 5);
        for (let i = 0; i < boltCount; i++) {
          this.spawnBolt(0, 1.0, 1.0);
        }
        // Final flash at 100%
        if (introProgress >= 0.98) {
          this.flashIntensity = Math.max(this.flashIntensity, 0.5);
        }
      }
      return;
    }

    // Massive beat burst - MORE DRAMATIC!
    if (this.currentBeat > 0.7) {
      const timeSinceLastBeat = this.currentTime - this.lastBeatTime;
      if (timeSinceLastBeat > 0.1) { // Prevent multiple triggers
        this.lastBeatTime = this.currentTime;
        this.flashIntensity = this.currentBeat * 0.35; // Stronger flash

        // Spawn 10-20 bolts on beat from center coil
        const boltCount = Math.floor(10 + this.currentBeat * 10 + this.currentEnergy * 5);
        for (let i = 0; i < boltCount; i++) {
          this.spawnBolt(0, this.currentBeat, 1.2);
        }
      }
    }

    // Bar mega-burst
    if (this.currentBar > 0.75) {
      this.flashIntensity = Math.max(this.flashIntensity, this.currentBar * 0.2);
      
      if (this.frameCount % 8 === 0) {
        this.spawnBolt(0, this.currentBar, 1.3);
      }
    }

    // Continuous spawning based on energy - more bolts since single coil
    const spawnRate = Math.floor(15 - this.currentEnergy * 12); // 3-15 frames
    if (this.frameCount % Math.max(3, spawnRate) === 0) {
      const desiredBolts = Math.floor(5 + this.currentEnergy * 20); // 5-25 bolts
      
      if (this.bolts.length < desiredBolts) {
        this.spawnBolt(0, this.currentEnergy, 0.8);
      }
    }

    // Frequency-based spawning - distribute based on pitches
    for (let i = 0; i < this.currentPitches.length; i++) {
      if (this.currentPitches[i] > 0.75 && Math.random() < 0.04) {
        this.spawnBolt(0, this.currentPitches[i], 0.7);
      }
    }
  }

  /**
   * Spawn a single lightning bolt from a coil
   */
  private spawnBolt(coilIndex: number, intensity: number, lengthMultiplier: number): void {
    if (this.bolts.length >= this.maxBolts * 2) { // Increased max bolts capacity
      // Remove oldest bolt
      this.bolts.shift();
    }

    const coil = this.coils[coilIndex];
    const startX = coil.x;
    const startY = coil.y;

    // 360-degree distribution - lightning shoots in ALL directions
    const angle = Math.random() * Math.PI * 2;
    const distance = (150 + Math.random() * 500) * lengthMultiplier * (0.6 + intensity * 0.5);
    
    // Pure radial - no bias, true 360 degrees
    const endX = startX + Math.cos(angle) * distance;
    const endY = startY + Math.sin(angle) * distance;

    // Determine color based on frequency
    const bassRatio = this.simulatedData.slice(0, 20).reduce((a, b) => a + b, 0) / 20 / 255;
    const trebleRatio = this.simulatedData.slice(-20).reduce((a, b) => a + b, 0) / 20 / 255;

    let hue: number;
    if (bassRatio > trebleRatio * 1.5) {
      // Bass - blue/purple
      hue = 240 + Math.random() * 40; // 240-280
    } else if (trebleRatio > bassRatio * 1.5) {
      // Treble - cyan/white
      hue = 180 + Math.random() * 30; // 180-210
    } else {
      // Mid - green/cyan
      hue = 160 + Math.random() * 60; // 160-220
    }

    const saturation = 70 + Math.random() * 30;
    const lightness = 60 + intensity * 20;

    const bolt = this.generateLightning(
      { x: startX, y: startY },
      { x: endX, y: endY },
      2.5 + intensity * 5, // Thicker bolts
      0,
      { h: hue, s: saturation, l: lightness },
      intensity
    );

    bolt.coilIndex = coilIndex;
    bolt.lifetime = 120 + Math.random() * 180; // Slightly longer lifetime
    bolt.age = 0;
    bolt.brightness = 0.8 + intensity * 0.2;

    this.bolts.push(bolt);
  }

  /**
   * Generate lightning path with branching
   */
  private generateLightning(
    from: { x: number; y: number },
    to: { x: number; y: number },
    thickness: number,
    depth: number,
    color: { h: number; s: number; l: number },
    intensity: number
  ): LightningBolt {
    const segments: { x: number; y: number }[] = [];
    const branches: LightningBolt[] = [];

    // Calculate total distance
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Number of segments based on distance
    const numSegments = Math.max(8, Math.floor(distance / 15));

    // Generate main path with jagged offsets
    segments.push({ x: from.x, y: from.y });

    for (let i = 1; i < numSegments; i++) {
      const t = i / numSegments;
      const baseX = from.x + dx * t;
      const baseY = from.y + dy * t;

      // Random perpendicular offset for jagged look
      const offsetAmount = 20 * (1 - t) * intensity; // Less offset near end
      const perpAngle = Math.atan2(dy, dx) + Math.PI / 2;
      const offsetX = Math.cos(perpAngle) * (Math.random() - 0.5) * offsetAmount;
      const offsetY = Math.sin(perpAngle) * (Math.random() - 0.5) * offsetAmount;

      segments.push({
        x: baseX + offsetX,
        y: baseY + offsetY
      });

      // Spawn branch with probability
      if (depth < 2 && Math.random() < 0.15 * intensity && i > 2) {
        const branchAngle = Math.atan2(dy, dx) + (Math.random() - 0.5) * Math.PI * 0.8;
        const branchLength = distance * (0.3 + Math.random() * 0.3);
        const branchEnd = {
          x: baseX + Math.cos(branchAngle) * branchLength,
          y: baseY + Math.sin(branchAngle) * branchLength
        };

        const branch = this.generateLightning(
          { x: baseX + offsetX, y: baseY + offsetY },
          branchEnd,
          thickness * 0.6,
          depth + 1,
          { h: color.h + (Math.random() - 0.5) * 20, s: color.s, l: color.l },
          intensity * 0.7
        );

        branches.push(branch);
      }
    }

    segments.push({ x: to.x, y: to.y });

    return {
      segments,
      branches,
      thickness,
      brightness: 1,
      color,
      lifetime: 0,
      age: 0,
      coilIndex: 0
    };
  }

  /**
   * Update all lightning bolts
   */
  private updateBolts(): void {
    const deltaTime = 16; // ~60fps

    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const bolt = this.bolts[i];
      bolt.age += deltaTime;

      // Fade out quickly
      const fadeProgress = bolt.age / bolt.lifetime;
      bolt.brightness = Math.max(0, 1 - fadeProgress);

      // Remove dead bolts
      if (bolt.brightness <= 0) {
        this.bolts.splice(i, 1);
      }
    }

    // Decay flash
    this.flashIntensity *= 0.85;
  }

  /**
   * Draw all lightning bolts
   */
  private drawBolts(): void {
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    for (const bolt of this.bolts) {
      this.drawLightningBolt(bolt);
    }
  }

  /**
   * Draw a single lightning bolt with glow
   */
  private drawLightningBolt(bolt: LightningBolt): void {
    const alpha = bolt.brightness;
    if (alpha <= 0.01) return;

    // Draw branches first (behind main bolt)
    for (const branch of bolt.branches) {
      this.drawLightningBolt(branch);
    }

    // Multi-pass glow effect
    // Pass 1: Thick outer glow
    this.ctx.strokeStyle = this.hsl(bolt.color.h, bolt.color.s, bolt.color.l, alpha * 0.15);
    this.ctx.lineWidth = bolt.thickness * 6;
    this.ctx.shadowBlur = 25;
    this.ctx.shadowColor = this.hsl(bolt.color.h, bolt.color.s, bolt.color.l + 20, alpha * 0.5);
    this.drawSegments(bolt.segments);

    // Pass 2: Medium inner glow
    this.ctx.strokeStyle = this.hsl(bolt.color.h, bolt.color.s, bolt.color.l + 10, alpha * 0.4);
    this.ctx.lineWidth = bolt.thickness * 3;
    this.ctx.shadowBlur = 15;
    this.drawSegments(bolt.segments);

    // Pass 3: Bright core with slight color
    this.ctx.strokeStyle = this.hsl(bolt.color.h, bolt.color.s * 0.5, 80, alpha * 0.7);
    this.ctx.lineWidth = bolt.thickness;
    this.ctx.shadowBlur = 8;
    this.drawSegments(bolt.segments);

    // Pass 4: White-hot core
    this.ctx.strokeStyle = this.rgb(255, 255, 255, alpha * 0.95);
    this.ctx.lineWidth = bolt.thickness * 0.3;
    this.ctx.shadowBlur = 5;
    this.ctx.shadowColor = this.rgb(200, 230, 255, alpha);
    this.drawSegments(bolt.segments);

    this.ctx.shadowBlur = 0;
  }

  /**
   * Draw path segments
   */
  private drawSegments(segments: { x: number; y: number }[]): void {
    if (segments.length < 2) return;

    this.ctx.beginPath();
    this.ctx.moveTo(segments[0].x, segments[0].y);

    for (let i = 1; i < segments.length; i++) {
      this.ctx.lineTo(segments[i].x, segments[i].y);
    }

    this.ctx.stroke();
  }

  /**
   * Draw Tesla coil orb (just a glowing sphere in center)
   */
  private drawCoils(introProgress: number): void {
    for (let i = 0; i < this.coils.length; i++) {
      const coil = this.coils[i];

      // Stagger appearance during intro
      const stagger = i * 0.15;
      const coilAlpha = this.isInIntro() 
        ? this.easeOutCubic(Math.max(0, Math.min(1, (introProgress - stagger) / (1 - stagger))))
        : 1;

      if (coilAlpha < 0.01) continue;

      this.ctx.save();
      this.ctx.globalAlpha *= coilAlpha;

      const glowIntensity = coil.glow;
      const glowHue = 200 + glowIntensity * 40;

      // Outer glow (large)
      if (glowIntensity > 0.1) {
        const gradient = this.ctx.createRadialGradient(
          coil.x, coil.y, 0,
          coil.x, coil.y, coil.baseRadius * (3 + glowIntensity * 3)
        );
        gradient.addColorStop(0, this.hsl(glowHue, 100, 60, glowIntensity * 0.6));
        gradient.addColorStop(0.4, this.hsl(glowHue, 100, 50, glowIntensity * 0.4));
        gradient.addColorStop(0.7, this.hsl(glowHue, 100, 40, glowIntensity * 0.2));
        gradient.addColorStop(1, this.hsl(glowHue, 100, 30, 0));

        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(coil.x, coil.y, coil.baseRadius * (3 + glowIntensity * 3), 0, Math.PI * 2);
        this.ctx.fill();
      }

      // Main orb sphere
      const orbGradient = this.ctx.createRadialGradient(
        coil.x - coil.baseRadius * 0.3,
        coil.y - coil.baseRadius * 0.3,
        0,
        coil.x,
        coil.y,
        coil.baseRadius
      );
      orbGradient.addColorStop(0, this.hsl(glowHue, 100, 70 + glowIntensity * 20, 0.9));
      orbGradient.addColorStop(0.5, this.hsl(glowHue, 90, 50 + glowIntensity * 30, 0.85));
      orbGradient.addColorStop(1, this.hsl(glowHue - 20, 80, 30 + glowIntensity * 20, 0.8));

      this.ctx.fillStyle = orbGradient;
      this.ctx.shadowBlur = 30 * glowIntensity;
      this.ctx.shadowColor = this.hsl(glowHue, 100, 60, glowIntensity);
      this.ctx.beginPath();
      this.ctx.arc(coil.x, coil.y, coil.baseRadius, 0, Math.PI * 2);
      this.ctx.fill();

      // Bright core
      if (glowIntensity > 0.3) {
        this.ctx.shadowBlur = 15 * glowIntensity;
        this.ctx.fillStyle = this.hsl(glowHue, 100, 90, glowIntensity * 0.9);
        this.ctx.beginPath();
        this.ctx.arc(coil.x, coil.y, coil.baseRadius * 0.5, 0, Math.PI * 2);
        this.ctx.fill();
      }

      this.ctx.shadowBlur = 0;
      this.ctx.restore();
    }
  }

  /**
   * Draw screen flash effect
   */
  private drawScreenFlash(): void {
    const gradient = this.ctx.createRadialGradient(
      this.canvas.width / 2,
      this.canvas.height / 2,
      0,
      this.canvas.width / 2,
      this.canvas.height / 2,
      Math.max(this.canvas.width, this.canvas.height)
    );

    gradient.addColorStop(0, this.hsl(200, 100, 80, this.flashIntensity * 0.8));
    gradient.addColorStop(0.5, this.hsl(220, 100, 70, this.flashIntensity * 0.4));
    gradient.addColorStop(1, this.hsl(240, 100, 50, 0));

    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Render idle state
   */
  private renderIdleState(): void {
    // Very dark background
    this.ctx.fillStyle = 'rgba(2, 0, 8, 1)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Update coil to low idle state
    const coil = this.coils[0];
    coil.energy = 0.08;
    coil.glow = 0.08 + Math.sin(this.currentTime * 2) * 0.04;

    // Draw barely glowing coil
    this.drawCoils(1.0);

    // Occasional tiny spark every 2 seconds
    if (this.frameCount % 120 === 0 && Math.random() < 0.5) {
      this.spawnBolt(0, 0.15, 0.2);
    }

    // Update and draw bolts
    this.updateBolts();
    this.drawBolts();

    // Hint text if never started
    if (!this.hasStartedOnce) {
      this.ctx.save();
      this.ctx.fillStyle = this.hsl(200, 60, 60, 0.4 + Math.sin(this.currentTime * 1.5) * 0.1);
      this.ctx.font = '28px monospace';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.shadowBlur = 10;
      this.ctx.shadowColor = this.hsl(200, 100, 60, 0.5);
      this.ctx.fillText('⚡ TESLA COIL VISUALIZER ⚡', this.canvas.width / 2, this.canvas.height / 2 - 50);
      this.ctx.font = '18px monospace';
      this.ctx.fillText('Play music to activate', this.canvas.width / 2, this.canvas.height / 2);
      this.ctx.shadowBlur = 0;
      this.ctx.restore();
    }
  }

  /**
   * Start intro animation
   */
  public startIntro(): void {
    if (this.visualizerState === 'running') {
      // Already running - create brief pulse from center (reduced from 8 to 3 bolts)
      this.flashIntensity = 0.25;
      for (let i = 0; i < 3; i++) {
        setTimeout(() => this.spawnBolt(0, 1.0, 1.2), i * 100);
      }
      return;
    }

    this.visualizerState = 'intro';
    this.introStartTime = this.currentTime;
    this.hasStartedOnce = true;

    console.log('⚡ Tesla coil powering up...');
  }

  /**
   * Check if in intro state
   */
  public isInIntro(): boolean {
    return this.visualizerState === 'intro';
  }

  /**
   * Get intro progress (0-1)
   */
  public getIntroProgress(): number {
    if (this.visualizerState !== 'intro') return 1;

    const elapsed = this.currentTime - this.introStartTime;
    return Math.min(1, elapsed / this.introDuration);
  }

  /**
   * Easing function: ease out cubic
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }
}
